import type { Course, PerHoleData, Player, Round, Score, TeeSet, Tournament } from "./types.js";
import { holesInPlay, holesPlayedCount } from "./handicap.js";
import { getFormat } from "./formats/index.js";
import type { RoundResult } from "./formats/types.js";

/** Scores every round of a tournament, given the courses its rounds reference.
 * Rounds whose course/tee set isn't available yet score as empty (no points).
 *
 * This is the same computation the live leaderboard uses. Two code paths that each
 * decided for themselves which holes counted is what let a closed round show points
 * on screen and zero in the export. */
export function computeRoundResults(
  tournament: Tournament,
  courses: Map<string, Course>,
  scores: Score[],
  perHoleData: PerHoleData[],
): RoundResult[] {
  return computeLiveRoundResults(tournament, courses, scores, perHoleData);
}

export interface LiveRoundResult extends RoundResult {
  /** True until the organiser closes the round, so the points are a running total.
   * A finished card is still provisional — finishing isn't signing off. */
  provisional: boolean;
  holesScored: number;
  holesTotal: number;
}

/**
 * Whether a round's points are final.
 *
 * Finality is a decision, not a hole count — in both directions. A group quits at 17
 * because of darkness, weather, or a twisted ankle, and the organiser closes the round:
 * those 17 holes are the result. Equally, a card with all 18 in isn't final until
 * somebody says so, because that's the moment scores get checked and corrected.
 *
 * So this is the only thing that settles a round, and it's entirely the organiser's
 * call: close the round, or end the game.
 */
export function isRoundFinal(round: Round, tournament: Tournament): boolean {
  return Boolean(round.locked) || tournament.status === "complete";
}

/**
 * Round results for the leaderboard. A round is scored over the holes everyone has
 * actually finished, and flagged `provisional` while it is still open, so the UI can
 * show a running figure rather than a result.
 *
 * Costs nothing extra on Cloudflare: this runs in the browser on the snapshot the
 * Durable Object already broadcasts after each score.
 */
export function computeLiveRoundResults(
  tournament: Tournament,
  courses: Map<string, Course>,
  scores: Score[],
  perHoleData: PerHoleData[],
): LiveRoundResult[] {
  return tournament.rounds.map((round) => {
    const course = courses.get(round.courseId);
    const teeSet = course?.teeSets.find((t) => t.name === round.teeSet);
    if (!course || !teeSet) {
      return { roundId: round.id, points: {}, provisional: false, holesScored: 0, holesTotal: 0 };
    }

    const holesTotal = holesPlayedCount(teeSet.holes, round.holeSet);
    const done = scorableHoles(round, course, tournament.players, scores);
    const fullCard = done.length === holesTotal;
    // Only the organiser closing the round (or ending the game) settles it — see
    // isRoundFinal. A complete card used to settle itself, which meant the standings
    // moved the instant the last putt was entered, before anyone had checked the card
    // for a fat-fingered score. Signing off is the organiser's job, and until they do
    // it the round stays live and its points stay in the projection.
    const final = isRoundFinal(round, tournament);

    // With nothing played yet every player is level on zero, and the tie-split would
    // hand out the average of the point table to the whole field before anyone has
    // teed off. A round with no completed holes simply has no points.
    if (done.length === 0) {
      const zero: Record<string, number> = {};
      for (const p of tournament.players) zero[p.id] = 0;
      return { roundId: round.id, points: zero, provisional: false, holesScored: 0, holesTotal };
    }

    const format = getFormat(round.formatId);
    const roundScores = scores.filter((s) => s.roundId === round.id);
    const roundPerHole = perHoleData
      .filter((d) => d.roundId === round.id)
      .map((d) => ({ hole: d.hole, ...d.data }));

    // `onlyHoles` is about which holes have scores, not about whether the result is
    // final. An abandoned round scored over all 18 finds a hole with no score, and
    // every format's total helper returns null for an incomplete card — so the whole
    // field drops out of the standings and the round pays nobody.
    const result = format.score({
      round,
      course: teeSet,
      players: tournament.players,
      scores: roundScores,
      perHole: roundPerHole,
      settled: final,
      ...(fullCard ? {} : { onlyHoles: done }),
    });

    return {
      ...applyMultiplier(result, round.multiplier),
      provisional: !final,
      holesScored: done.length,
      holesTotal,
    };
  });
}

/**
 * The round multiplier weights a whole round against the others. It's applied here
 * rather than inside each format so that every format honours it identically and a
 * new format can't forget to — formats return base points and the engine weights them.
 */
export function applyMultiplier(result: RoundResult, multiplier: number): RoundResult {
  if (!Number.isFinite(multiplier) || multiplier === 1) return result;

  const scale = (points: Record<string, number>): Record<string, number> =>
    Object.fromEntries(Object.entries(points).map(([id, v]) => [id, v * multiplier]));

  return {
    ...result,
    points: scale(result.points),
    ...(result.segments
      ? { segments: result.segments.map((s) => ({ ...s, points: scale(s.points) })) }
      : {}),
  };
}

/** Rules text for a round: the format's own generated lines, plus engine-level rules
 * the format doesn't know about (currently the round multiplier). */
export function describeRoundRules(
  round: Round,
  players: Player[],
  course?: TeeSet,
): string[] {
  const lines = getFormat(round.formatId).describeRules(round, players, course);
  if (round.multiplier !== 1 && Number.isFinite(round.multiplier)) {
    lines.push(
      `All points from this round are multiplied by ${round.multiplier} — it counts for ${round.multiplier}× a normal round.`,
    );
  }
  return lines;
}

/**
 * Holes that can be scored right now: every hole where all the scores the format
 * needs are already in. Scoring a partial round over *all* its holes just reports an
 * incomplete card as nothing, which is why a live leaderboard would otherwise sit at
 * zero until a round finished.
 *
 * What "all the scores" means differs by format: a single-ball team format needs one
 * per side, everything else needs one from every player involved. Team membership
 * comes from the segment covering that hole, since partners can change mid-round.
 */
export function scorableHoles(
  round: Round,
  course: Course,
  players: Player[],
  scores: Score[],
): number[] {
  const teeSet = course.teeSets.find((t) => t.name === round.teeSet);
  if (!teeSet) return [];

  const format = getFormat(round.formatId);
  // Via holesInPlay rather than a filter of its own: it is the one place that knows a
  // nine-hole card has no back nine to narrow to.
  const inPlay = holesInPlay(teeSet.holes, round.holeSet).map((h) => h.number);

  const have = new Set(
    scores
      .filter((s) => s.roundId === round.id && s.strokes != null)
      .map((s) => `${s.playerId}:${s.hole}`),
  );
  const scored = (id: string, hole: number) => have.has(`${id}:${hole}`);

  return inPlay.filter((hole, idx) => {
    const position = idx + 1;
    const segment = round.segments.find((sg) => position >= sg.startHole && position <= sg.endHole);

    if (format.entry === "perTeam") {
      // One shared score per side, recorded against whichever member entered it.
      // Empty sides (a team emptied out in the pairings editor) are excluded — an
      // empty array's `.some()` is vacuously false, which would otherwise make
      // `.every()` permanently false and lock the real sides out of ever scoring.
      if (!segment) return false;
      const sides = segment.teams.filter((team) => team.length > 0);
      return sides.length >= 2 && sides.every((team) => team.some((id) => scored(id, hole)));
    }

    // Everyone playing this hole needs their own score. With segments that's the
    // segment's teams; without them (individual formats) it's the whole field.
    const involved = segment ? segment.teams.flat() : players.map((p) => p.id);
    return involved.length > 0 && involved.every((id) => scored(id, hole));
  });
}

export interface RoundRulesText {
  roundId: string;
  roundName: string;
  rules: string[];
}

export function describeAllRoundRules(
  tournament: Tournament,
  courses?: Map<string, Course>,
): RoundRulesText[] {
  return tournament.rounds.map((round) => {
    const teeSet = courses
      ?.get(round.courseId)
      ?.teeSets.find((t) => t.name === round.teeSet);
    return {
      roundId: round.id,
      roundName: round.name,
      rules: getFormat(round.formatId).describeRules(round, tournament.players, teeSet),
    };
  });
}

/**
 * The round the group is out playing right now, or null if there isn't one.
 *
 * "Live" is scores-in-but-not-signed-off, which is narrower than "not settled": a
 * game with four rounds configured has three that nobody has teed off in, and
 * pointing a screen at the first of those would show an empty grid all afternoon.
 * Ordering follows the tournament's own round order, so the earliest round still in
 * play wins when a group is somehow spread across two.
 *
 * Lives here rather than in the clubhouse screen so it can be tested like the rest of
 * the scoring — the screen is the one thing in this app with no test harness.
 */
export function findLiveRound(
  tournament: Tournament,
  roundResults: LiveRoundResult[],
): Round | null {
  for (const round of tournament.rounds) {
    const result = roundResults.find((r) => r.roundId === round.id);
    // No result at all means the round's course hasn't loaded, so there is nothing
    // to draw yet either — skip it rather than showing a grid with no pars in it.
    if (!result) continue;
    if (!result.provisional) continue;
    if (result.holesScored === 0) continue;
    return round;
  }
  return null;
}
